/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { classnames as cx, cssUtils, propsUtils, keyboardUtils } from '@ba-ui-toolkit/utils';
import { Resizable } from 'react-resizable';
import SVGIcon from '@ba-ui-toolkit/svgicon';
import resizePanel24 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/resize-panel_24';
import Styles from './Container.scss';

const { sizeArrayToCss, mergeStyles } = cssUtils;
const { sanitize } = propsUtils;
const { isKey, keyNames } = keyboardUtils;
const containerClassName = Styles.containerClassName.replace(/"/g, '');
const KB_RESIZE_STEP = 5;

export default class Container extends Component {
	static propTypes = {
		gutter: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
		margin: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
		padding: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
		width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		overflow: PropTypes.string,
		relative: PropTypes.bool,
		children: PropTypes.node,
		textAlign: PropTypes.oneOf(['left', 'right', 'center']),
		style: PropTypes.object, // eslint-disable-line react/forbid-prop-types
		resizable: PropTypes.bool,
		resizeDirection: PropTypes.oneOf(['both', 'x', 'y', 'none']),
		resizeMinConstraintsX: PropTypes.number,
		resizeMinConstraintsY: PropTypes.number,
		resizeMaxConstraintsX: PropTypes.number,
		resizeMaxConstraintsY: PropTypes.number,
		onResize: PropTypes.func,
		onResizeStart: PropTypes.func,
		onResizeStop: PropTypes.func,
		className: PropTypes.string,
		id: PropTypes.string,
		resizeHandles: PropTypes.array,
	};

	static defaultProps = {
		resizable: false,
		resizeDirection: 'x',
		className: '',
		style: {},
		resizeMinConstraintsX: 32,
		resizeMinConstraintsY: 32,
		resizeMaxConstraintsX: Number.POSITIVE_INFINITY,
		resizeMaxConstraintsY: Number.POSITIVE_INFINITY,
		onResize: () => {},
		onResizeStart: () => {},
		onResizeStop: () => {},
	};

	constructor(props) {
		super(props);
		this.state = {
			width: 0,
			height: 0,
		};
	}

	componentDidMount() {
		const { resizable } = this.props;
		if (resizable) {
			this.setState({
				height: this._node.offsetHeight,
				width: this._node.offsetWidth,
			});
		}
	}

	componentDidUpdate(prevProps, prevState) {
		const { resizable } = this.props;
		if (
			resizable &&
			(prevState.height !== this._node.offsetHeight ||
				prevState.width !== this._node.offsetWidth)
		) {
			this.setState({
				height: this._node.offsetHeight,
				width: this._node.offsetWidth,
			});
		}
	}

	_onResize = (event, data) => {
		this.setState({ width: data.size.width, height: data.size.height });
		this.props.onResize(event, data);
	};

	_handler = h => (
		<span>
			<SVGIcon
				aria-label="resizable-container__handler"
				data-tid="ba-common-resizable-container__handler"
				className={`resize-handler-icon resize-handler-icon-${h}`}
				tabIndex="0"
				onKeyDown={this._onResizeHandlerKeyDown}
				iconId={resizePanel24.id}
			/>
			<span className={`react-resizable-handle react-resizable-handle-${h}`} />
		</span>
	);

	_onResizeHandlerKeyDown = e => {
		const { LEFT, RIGHT, UP, DOWN } = keyNames;
		const {
			resizeDirection,
			resizeMinConstraintsX,
			resizeMaxConstraintsX,
			resizeMinConstraintsY,
			resizeMaxConstraintsY,
		} = this.props;
		const { width, height } = this.state;
		if (isKey(e, LEFT) || isKey(e, RIGHT) || isKey(e, UP) || isKey(e, DOWN)) {
			if (resizeDirection === 'x') {
				if (isKey(e, LEFT) && resizeMinConstraintsX <= width - KB_RESIZE_STEP) {
					this.setState({ width: width - KB_RESIZE_STEP });
				} else if (isKey(e, RIGHT) && resizeMaxConstraintsX >= width + KB_RESIZE_STEP) {
					this.setState({ width: width + KB_RESIZE_STEP });
				}
			} else if (resizeDirection === 'y') {
				if (isKey(e, UP) && resizeMinConstraintsY <= height - KB_RESIZE_STEP) {
					this.setState({ height: height - KB_RESIZE_STEP });
				} else if (isKey(e, DOWN) && resizeMaxConstraintsY >= height + KB_RESIZE_STEP) {
					this.setState({ height: height + KB_RESIZE_STEP });
				}
			}
			e.preventDefault();
		}
	};

	_ref = node => {
		this._node = node;
	};

	render() {
		const {
			children,
			gutter,
			margin,
			padding,
			width,
			height,
			minWidth,
			minHeight,
			maxWidth,
			maxHeight,
			overflow,
			relative,
			textAlign,
			style,
			resizable,
			resizeDirection,
			resizeMinConstraintsX,
			resizeMinConstraintsY,
			resizeMaxConstraintsX,
			resizeMaxConstraintsY,
			className,
			onResizeStart,
			onResizeStop,
			id,
			resizeHandles,
			...others
		} = this.props;
		const styles = mergeStyles(style, {
			overflow,
			width,
			height,
			minWidth,
			minHeight,
			maxWidth,
			maxHeight,
			textAlign,
			margin: sizeArrayToCss(margin),
			padding: sizeArrayToCss(gutter || padding),
		});

		if (relative) {
			styles.position = 'relative';
		}
		const classNames = cx(containerClassName);
		const containerComponentClass = `${classNames} ${className}`;
		let defaultResizeHandles;
		if (resizable) {
			if (resizeHandles === undefined) {
				switch (resizeDirection) {
					case 'x':
						defaultResizeHandles = ['e'];
						break;
					case 'y':
						defaultResizeHandles = ['s'];
						break;
					case 'both':
						defaultResizeHandles = ['se'];
						break;
					default:
				}
			}

			return (
				<Resizable
					{...sanitize(others, Container)}
					axis={resizeDirection}
					height={this.state.height}
					width={this.state.width}
					onResize={this._onResize}
					onResizeStart={onResizeStart}
					onResizeStop={onResizeStop}
					minConstraints={[resizeMinConstraintsX, resizeMinConstraintsY]}
					maxConstraints={[resizeMaxConstraintsX, resizeMaxConstraintsY]}
					resizeHandles={
						resizeHandles === undefined ? defaultResizeHandles : resizeHandles
					}
					handle={this._handler}
				>
					<div
						id={id}
						className={cx(containerComponentClass, resizeDirection)}
						style={{
							...styles,
							width: this.state.width === 0 ? width : this.state.width,
							height: this.state.height === 0 ? height : this.state.height,
						}}
						ref={this._ref}
					>
						{children}
					</div>
				</Resizable>
			);
		}

		return (
			<div
				id={id}
				className={containerComponentClass}
				ref={this._ref}
				{...sanitize(others, Container)}
				style={styles}
			>
				{children}
			</div>
		);
	}
}
